<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import BzDialog from '~/components/bz/Dialog.vue'
    import { useGoogleDriveStore } from '~~/stores/google-drive'
    import type { PickerContext } from '~/composables/usePickerRegistry'

    const props = defineProps<{
        modelValue: string | null
        context?: PickerContext
        readonly?: boolean
        dense?: boolean
        outlined?: boolean
        borderless?: boolean
        label?: string
    }>()

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void
    }>()

    const driveStore = useGoogleDriveStore()
    const dialogOpen = ref(false)
    const resolvedPath = ref<string | null>(null)
    const resolving = ref(false)

    async function resolvePath() {
        if (!props.modelValue) {
            resolvedPath.value = null
            resolving.value = false
            return
        }
        resolving.value = true
        resolvedPath.value = await driveStore.resolveFolderPath(
            props.modelValue,
            props.context?.identityId,
        ).catch(() => null)
        resolving.value = false
    }

    watch(() => props.modelValue, resolvePath, { immediate: true })

    function onSelect(folderId: string) {
        emit('update:modelValue', folderId)
        dialogOpen.value = false
    }

    const identityId = computed(() => props.context?.identityId ?? null)
    const displayValue = computed(() => resolvedPath.value ?? '')

    const closeCommand = {
        execute: async () => {},
    }
</script>

<template>
    <!-- Read-only display mode -->
    <template v-if="readonly">
        <picker-display
            picker-name="google_folder"
            :value="modelValue"
            :context="context"
        />
    </template>

    <!-- Editable mode: q-input with folder icon appended -->
    <template v-else>
        <q-input
            :model-value="displayValue"
            :label="label ?? 'Folder'"
            :placeholder="!modelValue ? (resolving ? 'Loading…' : 'Select folder') : undefined"
            :dense="dense"
            :outlined="outlined"
            :borderless="borderless"
            readonly
            style="min-width: 180px; cursor: pointer"
            @click="dialogOpen = true"
        >
            <template #append>
                <q-spinner v-if="resolving" size="xs" color="grey" />
                <q-icon
                    v-else
                    name="folder_open"
                    class="cursor-pointer"
                    color="grey"
                    @click.stop="dialogOpen = true"
                />
            </template>
        </q-input>

        <bz-dialog
            v-model="dialogOpen"
            title="Select folder"
            cancel-label="Cancel"
            confirm-label="Close"
            :confirm-command="closeCommand"
            min-width="480px"
        >
            <google-drive-picker
                v-if="identityId && dialogOpen"
                :model-value="modelValue"
                :identity-id="identityId"
                :show-files="false"
                @update:model-value="onSelect"
            />
            <div
                v-else
                class="text-grey"
            >
                Select a Google identity first.
            </div>
        </bz-dialog>
    </template>
</template>
